var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import { Autocomplete, Box, Checkbox, FormControl, FormControlLabel, FormHelperText, FormLabel, InputAdornment, MenuItem, Radio, RadioGroup, TextField as MuiTextField, Tooltip, Typography, } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import moment from 'moment';
import AddressAutoCompleteField from './AddressAutoCompleteField';
import constants from '../../../config/constants';
import { FormActionIcon, FormFieldType, FormLabelPlacement, } from '../../../interfaces/createApplication';
import { CHECKBOX_HELPER_TEXT_SX, CHECKBOX_LABEL_SX, CHIP_GROUP_SX, dynamicActionButtonSx, dynamicBoxedControlSx, dynamicCheckboxSx, dynamicChipOptionSx, dynamicAboveLabelSx, dynamicChoiceLabelSx, dynamicFieldSx, dynamicFileButtonSx, dynamicFileNameSx, dynamicHelperTextSx, dynamicRadioControlSx, dynamicRadioErrorSx, dynamicRadioGroupSx, dynamicRadioLegendSx, dynamicRadioSx, dynamicTextAreaSx, FIELD_ERROR_TEXT_SX, FIELD_LABEL_SX, FILE_ROW_SX, FILE_STATUS_TEXT_SX, MENU_ITEM_SX, OPTION_DESCRIPTION_SX, OPTION_LABEL_SX, OPTION_WITH_DESCRIPTION_SX, RADIO_OPTION_LABEL_SX, REQUIRED_MARK_SX, SELECT_MENU_PROPS, SELECT_PLACEHOLDER_SX, START_ADORNMENT_SX, OCR_PREFILLED_ICON_SX, TOOLTIP_ICON_SX, } from './dynamic_form_styles';
var IN_FIELD_TOOLTIP_TYPES = [
    FormFieldType.Text,
    FormFieldType.Amount,
    FormFieldType.AddressAutoComplete,
];
var TOOLTIP_LABEL_SX = {
    '&:not(.MuiInputLabel-shrink)': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: 'calc(100% - 48px)',
    },
};
var DEFAULT_TEXT_MAX_LENGTH = 50;
var DEFAULT_TEXT_AREA_ROWS = 3;
var NO_FILE_TEXT = 'No File Choosen, Yet.';
var FULL_MASK_SUFFIXES = ['FedTaxId', 'TaxpayerId'];
var TAIL_MASK_SUFFIXES = ['PostalCode'];
var EDGE_MASK_SUFFIXES = [
    'Address',
    'Email',
    'Phone',
    'FirstName',
    'MiddleName',
    'LastName',
    'BusinessName',
    'City',
];
var endsWithFold = function (value, suffix) {
    return value.toLowerCase().endsWith(suffix.toLowerCase());
};
var getMaskStrategy = function (fieldName) {
    if (FULL_MASK_SUFFIXES.some(function (suffix) { return endsWithFold(fieldName, suffix); })) {
        return 'full';
    }
    if (TAIL_MASK_SUFFIXES.some(function (suffix) { return endsWithFold(fieldName, suffix); })) {
        return 'tail';
    }
    if (EDGE_MASK_SUFFIXES.some(function (suffix) { return endsWithFold(fieldName, suffix); })) {
        return 'edges';
    }
    return null;
};
var maskWithStars = function (rawValue, strategy) {
    if (!rawValue) {
        return rawValue;
    }
    if (strategy === 'full') {
        return '*'.repeat(rawValue.length);
    }
    if (strategy === 'tail') {
        return rawValue.length <= 2
            ? rawValue
            : '*'.repeat(rawValue.length - 2) + rawValue.slice(-2);
    }
    return rawValue.length <= 4
        ? rawValue
        : "".concat(rawValue.slice(0, 2)).concat('*'.repeat(rawValue.length - 4)).concat(rawValue.slice(-2));
};
var getMaskedDisplayValue = function (fieldName, fieldType, value, isLocked) {
    if (!isLocked || typeof value !== 'string') {
        return value;
    }
    if (fieldType !== FormFieldType.Text && fieldType !== FormFieldType.AddressAutoComplete) {
        return value;
    }
    var strategy = getMaskStrategy(fieldName);
    return strategy ? maskWithStars(value, strategy) : value;
};
var FieldLabel = function (_a) {
    var label = _a.label, isMandatory = _a.isMandatory, tooltip = _a.tooltip, isOcrPrefilled = _a.isOcrPrefilled;
    return (React.createElement(Box, { component: "span", sx: FIELD_LABEL_SX },
        isOcrPrefilled && React.createElement(CheckCircleIcon, { sx: OCR_PREFILLED_ICON_SX }),
        label,
        isMandatory && (React.createElement(Box, { component: "span", sx: REQUIRED_MARK_SX }, "*")),
        tooltip && (React.createElement(Tooltip, { title: tooltip, arrow: true, placement: "top" },
            React.createElement(InfoOutlinedIcon, { sx: TOOLTIP_ICON_SX })))));
};
var toMomentValue = function (value) {
    if (!value || typeof value !== 'string') {
        return null;
    }
    var parsed = moment(value, [constants.US_DATE_FORMAT, moment.ISO_8601], false);
    return parsed.isValid() ? parsed : null;
};
var findOptionByValue = function (options, value) { var _a; return (_a = options.find(function (option) { return String(option.value) === String(value !== null && value !== void 0 ? value : ''); })) !== null && _a !== void 0 ? _a : null; };
var DynamicFormField = function (_a) {
    var _b, _c, _d, _e;
    var field = _a.field, value = _a.value, error = _a.error, options = _a.options, isDisabled = _a.isDisabled, isLocked = _a.isLocked, onChange = _a.onChange, onBlur = _a.onBlur, onFileSelect = _a.onFileSelect, onAction = _a.onAction, onAutofill = _a.onAutofill, onSearchInput = _a.onSearchInput, onSearchSelect = _a.onSearchSelect, hasLabelSpacer = _a.hasLabelSpacer, isOcrPrefilled = _a.isOcrPrefilled;
    var fileInputRef = React.useRef(null);
    var hasError = Boolean(error);
    var isLabelAbove = field.labelPlacement === FormLabelPlacement.Above;
    var fieldName = field.name, actionKey = field.actionKey;
    var displayValue = getMaskedDisplayValue(fieldName, field.type, value, isLocked);
    var hasInFieldTooltip = Boolean(field.tooltip) &&
        !isDisabled &&
        !isLabelAbove &&
        IN_FIELD_TOOLTIP_TYPES.includes(field.type);
    var inputProps = React.useMemo(function () {
        var adornments = {};
        if (field.startAdornment) {
            adornments.startAdornment = (React.createElement(InputAdornment, { position: "start", sx: START_ADORNMENT_SX }, field.startAdornment));
        }
        if (hasInFieldTooltip) {
            adornments.endAdornment = (React.createElement(InputAdornment, { position: "end" },
                React.createElement(Tooltip, { title: field.tooltip, arrow: true, placement: "top" },
                    React.createElement(InfoOutlinedIcon, { sx: TOOLTIP_ICON_SX }))));
        }
        return Object.keys(adornments).length > 0 ? adornments : undefined;
    }, [field.startAdornment, field.tooltip, hasInFieldTooltip]);
    var controlPlaceholder = (_b = field.placeholder) !== null && _b !== void 0 ? _b : field.label;
    var labelNode = (React.createElement(FieldLabel, { label: field.label, isMandatory: field.isMandatory, tooltip: isDisabled ? undefined : field.tooltip, isOcrPrefilled: isOcrPrefilled }));
    var handleInputChange = React.useCallback(function (event) {
        onChange(fieldName, event.target.value);
    }, [fieldName, onChange]);
    var handleBlur = React.useCallback(function () {
        onBlur(fieldName);
    }, [fieldName, onBlur]);
    var _f = React.useState(false), isSelectActive = _f[0], setIsSelectActive = _f[1];
    var handleSelectFocus = React.useCallback(function () { return setIsSelectActive(true); }, []);
    var handleSelectBlur = React.useCallback(function () {
        setIsSelectActive(false);
        handleBlur();
    }, [handleBlur]);
    var handleAutoCompleteChange = React.useCallback(function (_event, selected) {
        if (selected === null) {
            onChange(fieldName, '');
            return;
        }
        if (typeof selected === 'string') {
            onChange(fieldName, selected);
            return;
        }
        if (field.searchKey) {
            onChange(fieldName, selected.label);
            onSearchSelect === null || onSearchSelect === void 0 ? void 0 : onSearchSelect(fieldName, selected);
            return;
        }
        onChange(fieldName, selected.value);
    }, [field.searchKey, fieldName, onChange, onSearchSelect]);
    var handleAutoCompleteInput = React.useCallback(function (_event, inputValue, reason) {
        if (reason !== 'input') {
            return;
        }
        if (field.isFreeSolo) {
            onChange(fieldName, inputValue);
        }
        onSearchInput === null || onSearchInput === void 0 ? void 0 : onSearchInput(fieldName, inputValue);
    }, [field.isFreeSolo, fieldName, onChange, onSearchInput]);
    var handleDateChange = React.useCallback(function (selectedDate) {
        var nextDate = selectedDate;
        onChange(fieldName, nextDate && nextDate.isValid() ? nextDate.format(constants.US_DATE_FORMAT) : '');
    }, [fieldName, onChange]);
    var handleRadioChange = React.useCallback(function (event) {
        var selected = options.find(function (option) { return String(option.value) === event.target.value; });
        onChange(fieldName, selected ? selected.value : event.target.value);
    }, [fieldName, onChange, options]);
    var handleCheckboxChange = React.useCallback(function (event) { return onChange(fieldName, event.target.checked); }, [fieldName, onChange]);
    var handleActionClick = React.useCallback(function () {
        if (actionKey) {
            onAction === null || onAction === void 0 ? void 0 : onAction(actionKey);
        }
    }, [actionKey, onAction]);
    var handleFileButtonClick = React.useCallback(function () { var _a; return (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click(); }, []);
    var handleFileInputChange = React.useCallback(function (event) {
        var _a, _b;
        onFileSelect === null || onFileSelect === void 0 ? void 0 : onFileSelect(fieldName, (_b = (_a = event.target.files) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : null);
        event.target.value = '';
    }, [fieldName, onFileSelect]);
    var needsLabelSpacer = Boolean(hasLabelSpacer) && !isLabelAbove;
    var withDisabledTooltip = function (node) {
        if (!isDisabled || isLocked || !field.disabledTooltip) {
            return node;
        }
        return (React.createElement(Tooltip, { title: field.disabledTooltip, arrow: true, placement: "top" },
            React.createElement("span", { style: { display: 'block', width: '100%' } }, node)));
    };
    var withDecoration = function (control) {
        var decorated = withDisabledTooltip(field.isBoxed ? React.createElement(Box, { sx: dynamicBoxedControlSx }, control) : control);
        if (!isLabelAbove && !field.helperText && !needsLabelSpacer) {
            return React.createElement(React.Fragment, null, decorated);
        }
        return (React.createElement(Box, null,
            isLabelAbove && (React.createElement(Typography, { component: "label", htmlFor: fieldName, sx: dynamicAboveLabelSx(hasError) }, labelNode)),
            needsLabelSpacer && (React.createElement(Typography, { "aria-hidden": true, sx: dynamicChoiceLabelSx }, "\u00A0")),
            decorated,
            field.helperText && (React.createElement(Typography, { sx: dynamicHelperTextSx }, field.helperText))));
    };
    var controlLabel = isLabelAbove ? undefined : hasInFieldTooltip ? (React.createElement(FieldLabel, { label: field.label, isMandatory: field.isMandatory, isOcrPrefilled: isOcrPrefilled })) : (labelNode);
    switch (field.type) {
        case FormFieldType.AddressAutoComplete: {
            return withDecoration(React.createElement(AddressAutoCompleteField, { field: field, value: displayValue, error: error, isDisabled: isDisabled, label: controlLabel, adornments: inputProps, onChange: onChange, onBlur: onBlur, onAutofill: onAutofill }));
        }
        case FormFieldType.Select: {
            var showsPlaceholder = isLabelAbove || isSelectActive;
            return withDecoration(React.createElement(MuiTextField, { select: true, fullWidth: true, size: "small", id: fieldName, name: fieldName, label: controlLabel, value: value !== null && value !== void 0 ? value : '', onChange: handleInputChange, onFocus: handleSelectFocus, onBlur: handleSelectBlur, disabled: isDisabled, error: hasError, helperText: error, sx: dynamicFieldSx, SelectProps: __assign({ displayEmpty: showsPlaceholder, renderValue: showsPlaceholder
                        ? function (selected) {
                            var _a;
                            var selectedOption = findOptionByValue(options, selected);
                            return selectedOption ? (React.createElement(React.Fragment, null, selectedOption.label)) : (React.createElement(Box, { component: "span", sx: SELECT_PLACEHOLDER_SX }, (_a = field.placeholder) !== null && _a !== void 0 ? _a : field.label));
                        }
                        : undefined }, SELECT_MENU_PROPS) },
                options.length === 0 && (React.createElement(MenuItem, { value: "", disabled: true }, "No Options")),
                options.map(function (option) { return (React.createElement(MenuItem, { key: String(option.value), value: option.value, sx: MENU_ITEM_SX }, option.label)); })));
        }
        case FormFieldType.AutoComplete: {
            var selectedOption = findOptionByValue(options, value);
            return withDecoration(React.createElement(Autocomplete, { id: fieldName, options: options, filterOptions: field.searchKey ? function (allOptions) { return allOptions; } : undefined, renderOption: options.some(function (option) { return option.description; })
                    ? function (optionProps, option) { return (React.createElement("li", __assign({}, optionProps, { key: String(option.value) }),
                        React.createElement(Box, { sx: OPTION_WITH_DESCRIPTION_SX },
                            React.createElement(Typography, { sx: OPTION_LABEL_SX }, option.label),
                            option.description && (React.createElement(Typography, { variant: "caption", sx: OPTION_DESCRIPTION_SX }, option.description))))); }
                    : undefined, freeSolo: field.isFreeSolo, disabled: isDisabled, size: "small", value: selectedOption !== null && selectedOption !== void 0 ? selectedOption : (field.isFreeSolo ? ((_c = value) !== null && _c !== void 0 ? _c : '') : null), getOptionLabel: function (option) {
                    return typeof option === 'string' ? option : option.label;
                }, isOptionEqualToValue: function (option, selected) {
                    return String(option.value) === String(selected === null || selected === void 0 ? void 0 : selected.value);
                }, onChange: handleAutoCompleteChange, onInputChange: handleAutoCompleteInput, onBlur: handleBlur, renderInput: function (params) { return (React.createElement(MuiTextField, __assign({}, params, { name: fieldName, label: controlLabel, placeholder: controlPlaceholder, error: hasError, helperText: error, sx: dynamicFieldSx }))); } }));
        }
        case FormFieldType.Date: {
            return withDecoration(React.createElement(DesktopDatePicker, { value: toMomentValue(value), format: constants.US_DATE_FORMAT, disabled: isDisabled, minDate: field.minDate ? moment(field.minDate) : undefined, maxDate: field.maxDate ? moment(field.maxDate) : undefined, onChange: handleDateChange, slotProps: {
                    textField: {
                        id: fieldName,
                        name: fieldName,
                        size: 'small',
                        fullWidth: true,
                        label: controlLabel,
                        error: hasError,
                        helperText: error,
                        onBlur: handleBlur,
                        sx: dynamicFieldSx,
                        inputProps: { autoComplete: 'off' },
                    },
                } }));
        }
        case FormFieldType.TextArea: {
            return withDecoration(React.createElement(MuiTextField, { fullWidth: true, multiline: true, minRows: (_d = field.rows) !== null && _d !== void 0 ? _d : DEFAULT_TEXT_AREA_ROWS, id: fieldName, name: fieldName, label: controlLabel, placeholder: controlPlaceholder, value: value !== null && value !== void 0 ? value : '', onChange: handleInputChange, onBlur: handleBlur, disabled: isDisabled, error: hasError, helperText: error, inputProps: { maxLength: field.maxLength }, InputLabelProps: isLabelAbove ? undefined : { shrink: true }, sx: dynamicTextAreaSx }));
        }
        case FormFieldType.Radio: {
            var isInline = Boolean(field.isInlineLabel);
            return withDecoration(React.createElement(FormControl, { error: hasError, disabled: isDisabled, component: "fieldset", variant: "standard", sx: dynamicRadioControlSx(isInline) },
                !isLabelAbove && (React.createElement(FormLabel, { component: isInline ? 'span' : 'legend', sx: dynamicRadioLegendSx(isInline) }, labelNode)),
                React.createElement(RadioGroup, { row: !field.isColumnLayout, name: fieldName, value: value === null || value === undefined ? '' : String(value), onChange: handleRadioChange, onBlur: handleBlur, sx: dynamicRadioGroupSx(isLabelAbove || isInline) }, options.map(function (option) { return (React.createElement(FormControlLabel, { key: String(option.value), value: String(option.value), control: React.createElement(Radio, { size: "small", sx: dynamicRadioSx }), label: React.createElement(Typography, { sx: RADIO_OPTION_LABEL_SX }, option.label) })); })),
                hasError && (React.createElement(FormHelperText, { sx: dynamicRadioErrorSx(isInline) }, error))));
        }
        case FormFieldType.ChipSelect: {
            return withDecoration(React.createElement(FormControl, { error: hasError, disabled: isDisabled, component: "fieldset", variant: "standard", fullWidth: true },
                React.createElement(Box, { role: "radiogroup", "aria-label": field.label, sx: CHIP_GROUP_SX }, options.map(function (option) {
                    var isSelected = String(option.value) === String(value !== null && value !== void 0 ? value : '');
                    return (React.createElement(Box, { key: String(option.value), component: "button", type: "button", role: "radio", "aria-checked": isSelected, disabled: isDisabled, onClick: function () { return onChange(fieldName, option.value); }, onBlur: handleBlur, sx: dynamicChipOptionSx(isSelected, hasError) }, option.label));
                })),
                hasError && React.createElement(FormHelperText, { sx: FIELD_ERROR_TEXT_SX }, error)));
        }
        case FormFieldType.CheckBox: {
            return (React.createElement(FormControl, { error: hasError, disabled: isDisabled, variant: "standard" },
                React.createElement(FormControlLabel, { control: React.createElement(Checkbox, { id: fieldName, name: fieldName, size: "small", sx: dynamicCheckboxSx, checked: Boolean(value), onChange: handleCheckboxChange, onBlur: handleBlur }), label: React.createElement(Typography, { sx: CHECKBOX_LABEL_SX },
                        field.label,
                        field.isMandatory && (React.createElement(Box, { component: "span", sx: REQUIRED_MARK_SX }, "*"))) }),
                field.helperText && (React.createElement(Typography, { sx: CHECKBOX_HELPER_TEXT_SX }, field.helperText)),
                hasError && React.createElement(FormHelperText, { sx: FIELD_ERROR_TEXT_SX }, error)));
        }
        case FormFieldType.Action: {
            return withDecoration(React.createElement(Box, { component: "button", type: "button", disabled: isDisabled, onClick: handleActionClick, sx: dynamicActionButtonSx(Boolean(field.isPrimaryAction), Boolean(field.isCompactAction)) },
                field.label,
                field.actionIcon === FormActionIcon.Search && (React.createElement(SearchIcon, { sx: { fontSize: 16 } }))));
        }
        case FormFieldType.File: {
            var fileName = typeof value === 'string' ? value : '';
            return (React.createElement(Box, null,
                React.createElement(Box, { sx: FILE_ROW_SX },
                    React.createElement(Box, { component: "button", type: "button", disabled: isDisabled, onClick: handleFileButtonClick, onBlur: handleBlur, sx: dynamicFileButtonSx(Boolean(isDisabled)) }, "Choose File"),
                    React.createElement(Box, { sx: dynamicFileNameSx(Boolean(fileName), hasError) }, fileName || field.placeholder || 'File Name')),
                React.createElement("input", { ref: fileInputRef, type: "file", hidden: true, accept: field.accept, "aria-label": field.label, onChange: handleFileInputChange }),
                field.helperText && (React.createElement(Typography, { sx: dynamicHelperTextSx }, field.helperText)),
                React.createElement(Typography, { sx: FILE_STATUS_TEXT_SX }, fileName || NO_FILE_TEXT),
                hasError && (React.createElement(FormHelperText, { error: true, sx: FIELD_ERROR_TEXT_SX }, error))));
        }
        default: {
            return withDecoration(React.createElement(MuiTextField, { fullWidth: true, size: "small", id: fieldName, name: fieldName, label: controlLabel, placeholder: controlPlaceholder, value: displayValue !== null && displayValue !== void 0 ? displayValue : '', onChange: handleInputChange, onBlur: handleBlur, disabled: isDisabled, error: hasError, helperText: error, inputProps: { maxLength: (_e = field.maxLength) !== null && _e !== void 0 ? _e : DEFAULT_TEXT_MAX_LENGTH }, InputProps: inputProps, InputLabelProps: isLabelAbove
                    ? undefined
                    : __assign({ shrink: undefined }, (hasInFieldTooltip && { sx: TOOLTIP_LABEL_SX })), sx: dynamicFieldSx }));
        }
    }
};
export default React.memo(DynamicFormField);
